import React, { useEffect, useMemo, useState, useRef, Children } from 'react';
import { CaretDown, CaretRight, MagnifyingGlass, Check } from '@phosphor-icons/react';
import { LOCATION_TREE } from '../../lib/constants';
import { cn } from '../../lib/utils';
interface StepLocationProps {
  location: string;
  onChange: (patch: {
    location: string;
  }) => void;
}
// Formats a structured location value like "București|Sector 1|Floreasca"
// or "Cluj-Napoca|Centru" into a human breadcrumb.
export function formatLocationBreadcrumb(value: string): string {
  if (!value) return '';
  return value.split('|').join(' · ');
}
// Walks LOCATION_TREE and finds the node matching the given value.
function findNodeByValue(value: string): any | null {
  if (!value) return null;
  const walk = (nodes: any[]): any | null => {
    for (const n of nodes) {
      if (n.value === value) return n;
      if (n.children) {
        const found = walk(n.children);
        if (found) return found;
      }
    }
    return null;
  };
  return walk(LOCATION_TREE as any);
}
export function StepLocation({
  location,
  onChange
}: StepLocationProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [expandedNodes, setExpandedNodes] = useState<Set<string>>(new Set(['București']));
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (ref.current && !ref.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  const toggleExpand = (e: React.MouseEvent, value: string) => {
    e.stopPropagation();
    setExpandedNodes(prev => {
      const next = new Set(prev);
      if (next.has(value)) next.delete(value);else next.add(value);
      return next;
    });
  };
  const pickNode = (node: any) => {
    // Single-select: pick this exact node (city, sector, or zone) as the
    // property location. A node with children is allowed too — picking a
    // city directly means "anywhere in the city".
    onChange({
      location: node.value
    });
    setIsOpen(false);
    setSearch('');
  };
  // Flatten tree for search.
  const flatNodes = useMemo(() => {
    const flat: any[] = [];
    const traverse = (nodes: any[]) => {
      nodes.forEach(n => {
        flat.push(n);
        if (n.children) traverse(n.children);
      });
    };
    traverse(LOCATION_TREE as any);
    return flat;
  }, []);
  const searchResults = useMemo(() => {
    if (!search.trim()) return null;
    const q = search.toLowerCase();
    return flatNodes.filter(n => n.label.toLowerCase().includes(q));
  }, [search, flatNodes]);
  const renderNode = (node: any, depth = 0) => {
    const isExpanded = expandedNodes.has(node.value);
    const hasChildren = !!node.children;
    const isSelected = location === node.value;
    return <div key={node.value} data-id="element-2586">
        <div className={cn('flex items-center gap-2 px-3 py-1.5 rounded-lg cursor-pointer group transition-colors', isSelected ? 'bg-dark-3' : 'hover:bg-dark-3')} style={{
        paddingLeft: `${depth * 16 + 12}px`
      }} onClick={() => pickNode(node)} data-id="element-2587">
          {hasChildren ? <button type="button" onClick={e => toggleExpand(e, node.value)} className="w-5 h-5 flex items-center justify-center text-light-4 hover:text-light-1 shrink-0" aria-label={isExpanded ? 'Restrânge' : 'Extinde'} data-id="element-2588">
              <CaretRight size={14} className={cn('transition-transform', isExpanded && 'rotate-90')} data-id="element-2589" />
            </button> : <div className="w-5 shrink-0" data-id="element-2590" />}

          <div className={cn('w-4 h-4 rounded-full border flex items-center justify-center shrink-0 transition-colors', isSelected ? 'border-lime-1' : 'border-light-3')} data-id="element-2591">
            {isSelected && <div className="w-2 h-2 rounded-full bg-lime-1" data-id="element-2592" />}
          </div>

          <span className={cn('text-sm truncate', depth === 0 ? 'text-light-1 font-medium' : 'text-light-2')} data-id="element-2593">
            {node.label}
          </span>
        </div>

        {hasChildren && isExpanded && <div className="mt-0.5" data-id="element-2594">
            {node.children.map((child: any) => renderNode(child, depth + 1))}
          </div>}
      </div>;
  };
  const triggerLabel = location ? formatLocationBreadcrumb(location) : 'Alege locația…';
  return <div className="animate-in fade-in slide-in-from-bottom-2 duration-300 space-y-8" data-id="element-2595">
      <div data-id="element-2596">
        <h2 className="text-2xl sm:text-3xl font-bold text-light-1 mb-2" data-id="element-2597">
          Unde se află?
        </h2>
        <p className="text-sm text-light-3" data-id="element-2598">
          Oraș, sector și cartier — folosim aceleași zone ca în Marketplace.
        </p>
      </div>

      <div className="relative" ref={ref} data-id="element-2599">
        <label className="block text-xs font-medium text-light-3 uppercase tracking-wider mb-2" data-id="element-2600">
          Locație
        </label>
        <button type="button" onClick={() => setIsOpen(v => !v)} aria-haspopup="listbox" aria-expanded={isOpen} className={cn('w-full h-11 px-4 rounded-md text-sm flex items-center justify-between transition-colors cursor-pointer', 'bg-dark-1 border', location ? 'border-lime-1 text-light-1' : 'border-dark-4 text-light-3 hover:border-light-4')} data-id="element-2601">
          <span className="truncate pr-2 text-left" data-id="element-2602">{triggerLabel}</span>
          <CaretDown size={14} weight="bold" className={cn('shrink-0 text-light-3 transition-transform', isOpen && 'rotate-180')} data-id="element-2603" />
        </button>

        {isOpen && <div className="absolute top-full left-0 right-0 mt-2 bg-dark-2 border border-white/15 rounded-xl z-50 overflow-hidden animate-in fade-in slide-in-from-top-2 flex flex-col max-h-[400px]" data-id="element-2604">
            <div className="p-3 border-b border-white/10 shrink-0" data-id="element-2605">
              <div className="relative" data-id="element-2606">
                <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-light-4" data-id="element-2607" />
                <input type="text" value={search} onChange={e => setSearch(e.target.value)} placeholder="Caută oraș, sector, zonă…" className="w-full h-9 bg-dark-3 border border-dark-4 rounded-lg pl-9 pr-3 text-sm text-light-1 placeholder:text-light-4 focus:outline-none focus:border-lime-1 focus:ring-1 focus:ring-lime-1" data-id="element-2608" />
              </div>
            </div>

            <div className="flex-1 overflow-y-auto p-2 dark-scrollbar" data-id="element-2609">
              {searchResults ? searchResults.length > 0 ? <div className="space-y-1" data-id="element-2610">
                    {searchResults.map(node => {
              const isSelected = location === node.value;
              return <button key={node.value} type="button" onClick={() => pickNode(node)} className={cn('w-full flex items-center gap-3 px-3 py-2 rounded-lg transition-colors text-left', isSelected ? 'bg-dark-3' : 'hover:bg-dark-3')} data-id="element-2611">
                          <div className={cn('w-4 h-4 rounded-full border flex items-center justify-center shrink-0 transition-colors', isSelected ? 'border-lime-1' : 'border-light-3')} data-id="element-2612">
                            {isSelected && <div className="w-2 h-2 rounded-full bg-lime-1" data-id="element-2613" />}
                          </div>
                          <div className="min-w-0" data-id="element-2614">
                            <div className="text-sm text-light-1 font-medium truncate" data-id="element-2615">
                              {node.label}
                            </div>
                            <div className="text-xs text-light-4 truncate" data-id="element-2616">
                              {node.value.replace(/\|/g, ' · ')}
                            </div>
                          </div>
                          {isSelected && <Check size={14} weight="bold" className="ml-auto text-lime-1 shrink-0" data-id="element-2617" />}
                        </button>;
            })}
                  </div> : <div className="py-8 text-center text-sm text-light-4" data-id="element-2618">
                    Nu am găsit rezultate pentru „{search}"
                  </div> : <div className="space-y-1" data-id="element-2619">
                  {(LOCATION_TREE as any[]).map(node => renderNode(node))}
                </div>}
            </div>
          </div>}
      </div>
    </div>;
}